import { useQuery } from '@tanstack/react-query';
import type { ConversionTableResource, ListConversionTablesResponse } from '@ab/ams-school-api/types/private/v1';

import { config } from '~/config';
import { useRequest } from '~/lib/useRequest';
import { IsNullOrUndefined } from '~/utils/isNullOrUndefined';

export type ConversionTablesSearchQuery = {
  'filter[ids]'?: string[];
  'filter[name]'?: string;
  'page[size]': number;
  'page[number]': number;
};

type UseConversionTablesOptions = {
  onSuccess?: (conversionTables: ConversionTableResource[], total: number) => void;
  keepPreviousData?: boolean;
  enabled?: boolean;
};

export function useConversionTables(query: ConversionTablesSearchQuery, options?: UseConversionTablesOptions) {
  const request = useRequest();
  const queryKey = ['conversionTables', query];

  const { isLoading, isFetching, data, error, isPreviousData } = useQuery(
    queryKey,
    async () => {
      const queryParams = convertToUrlSearchParams(query);
      const response = await request<ListConversionTablesResponse>(
        `${config.apiHost}/private/v1/conversion_tables?${queryParams}`,
        {
          method: 'GET',
          isExpectedResponse,
        }
      );

      return response;
    },
    {
      keepPreviousData: options?.keepPreviousData ?? true,
      enabled: options?.enabled ?? true,
      onSuccess: (response) => {
        options?.onSuccess?.(response.data, response.meta.pagination.total);
      },
    }
  );

  return {
    conversionTables: data?.data ?? [],
    conversionTablesPagination: data?.meta.pagination,
    totalConversionTables: data?.meta.pagination?.total ?? 0,
    isPreviousConversionTablesData: isPreviousData,
    conversionTablesError: error,
    isFetchingConversionTables: isFetching,
    isLoadingConversionTables: isLoading,
  };
}

const convertToUrlSearchParams = (params: ConversionTablesSearchQuery, baseKey?: string) => {
  const queryParams = {} as Record<string, string>;
  (Object.entries(params) as Array<[string, string | number | string[]]>).forEach(([k, v]) => {
    const key = (baseKey && `${baseKey}[${k}]`) || k;
    if (IsNullOrUndefined(v)) {
      return;
    }
    if (typeof v === 'string' && v.length > 0) {
      queryParams[key] = v;
    } else if (typeof v === 'number') {
      queryParams[key] = `${v}`;
    } else if (Array.isArray(v) && v.length > 0) {
      queryParams[key] = v.join(',');
    }
  });

  queryParams['include'] = 'education_level,grade_schema';

  return new URLSearchParams(queryParams);
};

function isExpectedResponse(res: unknown): res is ListConversionTablesResponse {
  if (!res || typeof res !== 'object') {
    return false;
  }
  const candidate = res as ListConversionTablesResponse;
  return Boolean(candidate.data && Array.isArray(candidate.data));
}
